import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  InputLabel,
  InputLabelProps,
  Select,
  SelectProps,
} from '@mui/material'
import { FieldValues, Path, PathValue, useController, UseControllerProps } from 'react-hook-form'
import { ElementType, useMemo } from 'react'

export interface HookFormSelectProps<T extends FieldValues> extends Omit<
  SelectProps,
  'value' | 'onChange' | 'name' | 'slots' | 'slotProps'
> {
  name: Path<T>
  helperText?: string
  defaultValue?: PathValue<T, Path<T>>
  control?: UseControllerProps<T>['control']
  slots?: {
    formControl?: ElementType
    inputLabel?: ElementType
    formHelperText?: ElementType
  } & SelectProps['slots']
  slotProps?: {
    formControl?: Partial<FormControlProps>
    inputLabel?: Partial<InputLabelProps>
    formHelperText?: Partial<FormHelperTextProps>
  } & SelectProps['slotProps']
}

const defaultSlots = {
  formControl: FormControl,
  inputLabel: InputLabel,
  formHelperText: FormHelperText,
}

const defaultSlotProps = {
  formControl: {},
  inputLabel: {},
  formHelperText: {},
}

export function HookFormSelect<T extends FieldValues>({
  name,
  label,
  helperText,
  defaultValue,
  control,
  fullWidth,
  size,
  slots: slotsProp,
  slotProps: slotPropsProp,
  ...props
}: HookFormSelectProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, defaultValue, control })

  const {
    formControl: FormControlSlot,
    inputLabel: InputLabelSlot,
    formHelperText: FormHelperTextSlot,
    ...restSlots
  } = useMemo(
    () => ({
      ...defaultSlots,
      ...slotsProp,
    }),
    [slotsProp],
  )
  const {
    formControl: formControlProps,
    inputLabel: inputLabelProps,
    formHelperText: formHelperTextProps,
    ...restSlotProps
  } = useMemo(
    () => ({
      ...defaultSlotProps,
      ...slotPropsProp,
    }),
    [slotPropsProp],
  )

  return (
    <FormControlSlot {...formControlProps} error={!!error} fullWidth={fullWidth} size={size}>
      {label && <InputLabelSlot {...inputLabelProps}>{label}</InputLabelSlot>}
      <Select
        label={label}
        {...props}
        fullWidth={fullWidth}
        size={size}
        slots={restSlots}
        slotProps={restSlotProps}
        {...field}
      />
      {(error || helperText) && (
        <FormHelperTextSlot {...formHelperTextProps} error={!!error}>
          {error ? error.message : helperText}
        </FormHelperTextSlot>
      )}
    </FormControlSlot>
  )
}
